import { createService } from 'v2/shared/services/create-service'
import { BackendServiceName } from 'v2/types'
import type { AuthService } from 'v2/types/api/auth'

const AUTH_ENDPOINTS = {
  login: '/login',
  loginv2: '/login-v2',
  // Plivo CLI loopback-OAuth (PKCE) browser-login. The /cli/authorize page
  // calls this with the user's Console session bearer to mint a one-time
  // code; see hodor's controllers/cliAuth for the full protocol.
  cliExchange: '/cli/exchange',
  cliReject: '/cli/reject',
  logout: '/logout',
  // Invite User V2 endpoints
  inviteUserV2: '/invite-user',
  activateV2: '/activate-invite-user',
  // Internal support user endpoints
  inviteInternalUser: '/invite-internal-user',
  supportUserStatus: '/support-user-status',
  'two-factor-auth': '/signup/phone-verification',
  'two-factor-verify': '/verify-otp',
  'two-factor-resend': '/signup/phone-verification/resend',
  initiateSignup: '/initiate-signup',
  resendActivationEmail: '/resend-activation-email',
  completeSignup: '/complete-signup',
  completeInviteSignup: '/complete-invite-signup',
  validateInviteSignupToken: '/invite-signup/validate',
  createOrganization: '/create-organization',
  preSignupCheck: '/pre-signup-check',
  // Password V2 endpoints
  'reset-password-v2': '/set-password-v2',
  'change-password-v2': '/change-password-v2',
  'forgot-password-v2': '/reset-password-v2',
  'validate-reset-token-v2': '/set-password-v2',
  // Email change endpoints
  'request-email-change': '/email-change',
  'resend-email-change-otp': '/email-change/resend-otp',
  'verify-email-change': '/email-change/verify',
}

// Tell the browser HTTP cache not to store/serve this GET. The pre-signup
// check (VPN/proxy/region/IP) must re-run against the live connection, else a
// stale "blocked" 200 keeps showing after the user disconnects their VPN and
// does a normal refresh (SER-5734). Mirrors billing-service's NO_CACHE_CONFIG.
const NO_CACHE_CONFIG = {
  headers: {
    'Cache-Control': 'no-cache, no-store, must-revalidate',
    Pragma: 'no-cache',
    Expires: '0',
  },
}

/**
 * Creates an instance of the AuthService.
 * @returns An object with methods for authentication.
 */
const serviceFactory = (): AuthService => {
  const service = createService('accounts', false, BackendServiceName.default, 'v1')
  return {
    login: (data) => service.post(AUTH_ENDPOINTS.login, data),
    loginv2: (data) => service.post(AUTH_ENDPOINTS.loginv2, data),
    cliExchange: (data) => service.post(AUTH_ENDPOINTS.cliExchange, data),
    cliReject: (data) => service.post(AUTH_ENDPOINTS.cliReject, data),
    logout: () =>
      service.delete(AUTH_ENDPOINTS.logout, undefined, {
        validateStatus: (status) => status === 200 || status === 401,
      }),
    // Invite User V2 endpoints
    inviteUserV2: (data) => service.post(AUTH_ENDPOINTS.inviteUserV2, data),
    activateV2: (data) => service.post(AUTH_ENDPOINTS.activateV2, data),
    // Internal support user endpoints
    inviteInternalUser: (data) => service.post(AUTH_ENDPOINTS.inviteInternalUser, data),
    getSupportUserStatus: (data) =>
      service.post(AUTH_ENDPOINTS.supportUserStatus, data, undefined, {
        validateStatus: (status: number) => status === 200 || status === 404,
      }),
    twoFactorAuth: (data) => service.post(AUTH_ENDPOINTS['two-factor-auth'], data),
    twoFactorVerify: (data) => service.post(AUTH_ENDPOINTS['two-factor-verify'], data),
    twoFactorResend: (data) => service.post(AUTH_ENDPOINTS['two-factor-resend'], data),
    initiateSignup: (data) => service.post(AUTH_ENDPOINTS.initiateSignup, data),
    resendActivationEmail: (data) => service.post(AUTH_ENDPOINTS.resendActivationEmail, data),
    completeSignup: (data) => service.post(AUTH_ENDPOINTS.completeSignup, data),
    completeInviteSignup: (data) => service.post(AUTH_ENDPOINTS.completeInviteSignup, data),
    validateInviteSignupToken: (token) =>
      service.get(AUTH_ENDPOINTS.validateInviteSignupToken, { token }),
    createOrganization: (data) => service.post(AUTH_ENDPOINTS.createOrganization, data),
    preSignupCheck: (tz, couponCode) =>
      service.get(
        AUTH_ENDPOINTS.preSignupCheck,
        {
          ...(tz && { tz }),
          ...(couponCode && { coupon_code: couponCode }),
        },
        NO_CACHE_CONFIG,
      ),
    // Password V2 methods
    resetPasswordV2: (data) => service.post(AUTH_ENDPOINTS['reset-password-v2'], data),
    changePasswordV2: (data) => service.post(AUTH_ENDPOINTS['change-password-v2'], data),
    forgotPasswordV2: (data) => service.post(AUTH_ENDPOINTS['forgot-password-v2'], data),
    validateResetTokenV2: (data) =>
      service.post(AUTH_ENDPOINTS['validate-reset-token-v2'], data, undefined, {
        validateStatus: (status: number) => status === 200 || status === 400,
      }),
    // Email change methods
    // 4xx bodies carry errors.global_error. 401 stays rejected so the session-expiry
    // interceptor logs out; 429 stays rejected for the modal's rate-limit copy.
    requestEmailChange: (data) =>
      service.post(AUTH_ENDPOINTS['request-email-change'], data, undefined, {
        validateStatus: (status: number) =>
          status === 200 || (status >= 400 && status < 500 && status !== 401 && status !== 429),
      }),
    verifyEmailChange: (data) =>
      service.post(AUTH_ENDPOINTS['verify-email-change'], data, undefined, {
        validateStatus: (status: number) =>
          status === 200 || (status >= 400 && status < 500 && status !== 401),
      }),
  }
}

/**
 * A service instance for the authentication API or the Hodor Service (Global)
 * @returns Available methods for authentication.
 */
export const authService = serviceFactory()
